import React, { useState, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Send, FileText, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import StatusUpgradeConflictDialog from './StatusUpgradeConflictDialog';
import { useEstimateVersioningEnhanced } from './hooks/useEstimateVersioningEnhanced';

type VersionStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'cancelled';

interface VersionStatusManagerProps {
  versionId: string;
  currentStatus: VersionStatus;
  versionType: 'estimate' | 'change_order';
  versionNumber: number;
  estimateId?: string;
  onStatusChange?: (newStatus: VersionStatus) => void;
}

const VersionStatusManager: React.FC<VersionStatusManagerProps> = ({
  versionId,
  currentStatus,
  versionType,
  versionNumber,
  estimateId,
  onStatusChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<VersionStatus>(currentStatus);
  const [notes, setNotes] = useState('');
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [pendingConflicts, setPendingConflicts] = useState<any>(null);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

  const { checkStatusUpgradeConflicts, resolveStatusConflicts } = useEstimateVersioningEnhanced();

  // Fetch invoices for this estimate to check if status downgrade should be blocked
  const { data: invoices } = useQuery({
    queryKey: ['versionInvoices', estimateId],
    queryFn: async () => {
      if (!estimateId) return [];
      const { data, error } = await supabase
        .from('invoices')
        .select('id, status, paid_amount')
        .eq('estimate_id', estimateId);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!estimateId,
    staleTime: 30000,
  });

  // Check if downgrade to draft should be blocked
  const hasPaidInvoices = invoices?.some(inv => inv.status === 'paid' || (inv.paid_amount && inv.paid_amount > 0));
  const hasSentInvoices = invoices?.some(inv => inv.status === 'sent');
  const hasAnyInvoices = invoices && invoices.length > 0;
  const isDraftBlocked = hasAnyInvoices;
  
  // Cancelled versions are completely locked
  const isCancelledLocked = currentStatus === 'cancelled';

  // Reset status when dialog opens to ensure it matches the current version
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setNewStatus(currentStatus);
      setNotes('');
    }
  };

  // Update newStatus when currentStatus prop changes and modal opens
  React.useEffect(() => {
    if (isOpen) {
      setNewStatus(currentStatus);
    }
  }, [currentStatus, isOpen]);
  
  const queryClient = useQueryClient();

  const updateStatusMutation = useMutation({
    mutationFn: async ({ status, notes }: { status: VersionStatus; notes?: string }) => {
      console.log('🔄 Direct status update (VersionStatusManager):', { versionId, status });
      
      // Get estimate_id and old main version before update
      const { data: versionData, error: versionError } = await supabase
        .from('estimate_versions')
        .select('estimate_id')
        .eq('id', versionId)
        .single();

      if (versionError) throw versionError;
      
      const { data: oldEstimate } = await supabase
        .from('estimates')
        .select('main_version_id')
        .eq('id', versionData.estimate_id)
        .single();

      // Direct update to estimate_versions - triggers will handle status_changed_at and main recomputation
      const { error } = await supabase
        .from('estimate_versions')
        .update({ 
          status: status,
          // Let the trigger handle status_changed_at automatically
        })
        .eq('id', versionId);

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      // When status is 'sent', update expires_at and sent_date
      if (status === 'sent') {
        // Fetch validity_period from the estimate
        const { data: estimateData } = await supabase
          .from('estimates')
          .select('validity_period, company_id')
          .eq('id', versionData.estimate_id)
          .single();

        let validityPeriod = estimateData?.validity_period;

        // Fallback to document_defaults if validity_period is null
        if (validityPeriod == null && estimateData?.company_id) {
          const { data: defaults } = await supabase
            .from('document_defaults')
            .select('estimate_validity_days')
            .eq('company_id', estimateData.company_id)
            .single();
          validityPeriod = defaults?.estimate_validity_days ?? null;
        }

        // Final fallback to 14
        validityPeriod = validityPeriod ?? 14;

        await supabase
          .from('estimates')
          .update({
            expires_at: new Date(Date.now() + (validityPeriod * 24 * 60 * 60 * 1000)).toISOString()
          })
          .eq('id', versionData.estimate_id);

        await supabase
          .from('estimate_versions')
          .update({ sent_date: new Date().toISOString() })
          .eq('id', versionId);
      }

      // Get new main version after update to check if it changed
      const { data: newEstimate } = await supabase
        .from('estimates')
        .select('main_version_id')
        .eq('id', versionData.estimate_id)
        .single();

      const mainVersionChanged = oldEstimate?.main_version_id !== newEstimate?.main_version_id;
      console.log('📊 Main version change (VersionStatusManager):', {
        old: oldEstimate?.main_version_id,
        new: newEstimate?.main_version_id,
        changed: mainVersionChanged,
        isNowMain: newEstimate?.main_version_id === versionId
      });

      // Fetch the updated version data to return for optimistic updates
      const { data: updatedVersionData, error: fetchError } = await supabase
        .from('estimate_versions')
        .select('*, estimate_id')
        .eq('id', versionId)
        .single();

      if (fetchError) throw fetchError;
      return { 
        ...updatedVersionData, 
        updateResult: { 
          main_version_changed: mainVersionChanged,
          old_main_version_id: oldEstimate?.main_version_id,
          new_main_version_id: newEstimate?.main_version_id
        }
      };
    },
    onSuccess: async (data) => {
      // Get estimate_id from the version for targeted query invalidation
      const estimate_id = data.estimate_id;
      
      // Comprehensive query invalidation to ensure UI consistency
      const queriesToInvalidate = [
        ['estimateVersions', estimate_id],
        ['estimate', estimate_id],
        ['estimates'], // Always invalidate estimates list
        ['invoices'],
        ['invoice'],
        ['estimateLineItems'],
        ['estimateForInvoicing'],
        ['projectInvoices'],
      ];
      
      // If approved, also invalidate project and dashboard queries
      if (data.status === 'approved') {
        queriesToInvalidate.push(
          ['dashboard-data'],
          ['projects'],
          ['project'], // Invalidate all individual project queries
          ['estimateProject', estimate_id],
          ['activity-logs']
        );

        // Get the specific project ID for this estimate to invalidate its specific query
        try {
          const { data: projectData } = await supabase
            .from('projects')
            .select('id')
            .eq('estimate_id', estimate_id)
            .single();
          
          if (projectData?.id) {
            queriesToInvalidate.push(['project', projectData.id]);
          }
        } catch (error) {
          console.warn('Could not fetch project ID for specific invalidation:', error);
        }
      }
      
      // Optimistic UI update first
      queryClient.setQueryData(['estimateVersions', estimate_id], (old: any) => {
        if (!old) return old;
        return old.map((version: any) => 
          version.id === versionId ? { ...version, status: newStatus } : version
        );
      });
      
      // Invalidate and refetch with staggered timing for database consistency
      await queryClient.invalidateQueries({ queryKey: ['estimate', estimate_id] });
      await queryClient.invalidateQueries({ queryKey: ['estimateVersions', estimate_id] });
      
      // Short delay then force refetch to ensure UI reflects database state
      setTimeout(async () => {
        await queryClient.refetchQueries({ queryKey: ['estimate', estimate_id] });
        await queryClient.refetchQueries({ queryKey: ['estimateVersions', estimate_id] });
      }, 300);
      
      let message = `${versionType === 'change_order' ? 'Change order' : 'Version'} status updated to ${data.status}`;
      
      // Check if main version changed
      if (data.updateResult?.main_version_changed) {
        if (data.updateResult.new_main_version_id === versionId) {
          message += ' and is now the main version';
        } else if (data.updateResult.old_main_version_id === versionId) {
          message += ' and is no longer the main version';
        }
      }
      
      toast.success(message);
      onStatusChange?.(data.status as VersionStatus);
      setIsOpen(false);
      setNotes('');
    },
    onError: (error: any) => {
      console.error('Error updating version status:', error);
      // Check for specific trigger error messages
      if (error.message?.includes('another') && error.message?.includes('approved')) {
        toast.error('Cannot approve: another version is already approved. Please cancel or reject it first.');
      } else if (error.message?.includes('invoice')) {
        toast.error('Cannot revert to draft: this estimate has active invoices.');
      } else {
        toast.error('Failed to update version status');
      }
    },
  });

  const handleStatusUpdate = async () => {
    if (newStatus === currentStatus) {
      setIsOpen(false);
      return;
    }
    
    // If upgrading to 'approved', check for conflicts first
    if (newStatus === 'approved' && estimateId) {
      setIsCheckingConflicts(true);
      try {
        const conflicts = await checkStatusUpgradeConflicts(estimateId, 'approved', versionId);
        
        // If there are approved conflicts, show conflict dialog
        if (conflicts.approved_versions.length > 0) {
          setPendingConflicts(conflicts);
          setShowConflictDialog(true);
          setIsCheckingConflicts(false);
          return;
        }
      } catch (error) {
        console.error('Error checking conflicts:', error);
        toast.error('Failed to check for conflicts');
        setIsCheckingConflicts(false);
        return;
      }
      setIsCheckingConflicts(false);
    }
    
    updateStatusMutation.mutate({ status: newStatus, notes });
  };

  const handleConflictResolve = async (resolution: any) => {
    if (!estimateId) return;
    
    try {
      // Resolve conflicts first
      await resolveStatusConflicts(estimateId, resolution);
      
      // Then proceed with the status update
      setShowConflictDialog(false);
      setPendingConflicts(null);
      updateStatusMutation.mutate({ status: newStatus, notes });
    } catch (error) {
      console.error('Error resolving conflicts:', error);
      toast.error('Failed to resolve conflicts');
    }
  };

  const getStatusIcon = (status: VersionStatus) => {
    switch (status) {
      case 'sent':
        return <Send className="h-4 w-4" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getStatusLabel = (status: VersionStatus) => {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'sent':
        return 'Sent to Customer';
      case 'approved':
        return 'Approved by Customer';
      case 'rejected':
        return 'Rejected by Customer';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const getAvailableStatuses = (): VersionStatus[] => {
    // Cancelled versions are completely locked - no status changes allowed
    if (isCancelledLocked) {
      return [currentStatus];
    }
    
    // Filter out draft if invoices exist
    const allStatuses: VersionStatus[] = ['draft', 'sent', 'approved', 'rejected', 'cancelled'];
    
    if (isDraftBlocked) {
      return allStatuses.filter(s => s !== 'draft');
    }
    
    return allStatuses;
  };

  const availableStatuses = getAvailableStatuses();
  const canChangeStatus = availableStatuses.length > 1 && !isCancelledLocked;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            disabled={!canChangeStatus}
            className="flex items-center gap-2"
          >
            {getStatusIcon(currentStatus)}
            Update Status
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              Update {versionType === 'change_order' ? 'Change Order' : 'Version'} Status
            </DialogTitle>
            <DialogDescription>
              Change the status of {versionType === 'change_order' ? 'change order' : 'version'} v{versionNumber} to track its progress.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="status">New Status</Label>
              <Select value={newStatus} onValueChange={(value: VersionStatus) => setNewStatus(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {availableStatuses.map((status) => (
                    <SelectItem key={status} value={status}>
                      {getStatusLabel(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any notes about this status change..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            {isCancelledLocked && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Cancelled versions are locked and cannot have their status changed.
                </AlertDescription>
              </Alert>
            )}
            {isDraftBlocked && !isCancelledLocked && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Draft status is unavailable because this estimate has {hasPaidInvoices ? 'paid' : hasSentInvoices ? 'sent' : ''} invoices.
                </AlertDescription>
              </Alert>
            )}
            {newStatus === 'approved' && currentStatus !== 'approved' && !isCancelledLocked && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Only one approved version is allowed. If another version is already approved, you'll need to cancel or reject it first.
                </AlertDescription>
              </Alert>
            )}
            {newStatus !== currentStatus && (
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-800">
                  Status will change from <strong>{getStatusLabel(currentStatus)}</strong> to{' '}
                  <strong>{getStatusLabel(newStatus)}</strong>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleStatusUpdate}
              disabled={updateStatusMutation.isPending || isCheckingConflicts}
            >
              {isCheckingConflicts ? 'Checking...' : updateStatusMutation.isPending ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      {pendingConflicts && (
        <StatusUpgradeConflictDialog
          isOpen={showConflictDialog}
          onClose={() => {
            setShowConflictDialog(false);
            setPendingConflicts(null);
          }}
          conflicts={pendingConflicts}
          newStatus="approved"
          onResolve={handleConflictResolve}
        />
      )}
    </>
  );
};

export default VersionStatusManager;